import React from 'react';
import { RefreshControl, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import { ROLE_PERMISSIONS } from '../../../common/constants';
import { useUserContext } from '../../../common/utils/context';
import { formatShiftRange } from '../../../common/utils/people/roster';
import { printMillisecondsNicely } from '../../../common/utils/time/time';
import { formatNumber } from '../../../common/utils/utils';
import { TextInput } from '../../components';
import Text from '../../components/text/Text';
import {
	Card,
	EmptyState,
	ListRow,
	PrimaryButton,
	Screen,
	SecondaryButton,
	SectionTitle,
	SegmentedControl,
	StatusBadge,
	Tag,
} from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import PickerLabelDialogs from '../myDay/PickerLabelDialogs';
import usePickerMyDay from '../myDay/usePickerMyDay';
import { useDriverShell } from './DriverDayProvider';
import { APP_TAB, DRIVER_ROUTE, TAB_BAR_TABS } from './driverRoutes';
import PickingRouteGroup from './picking/PickingRouteGroup';

const formatCount = (count, singular, plural) => `${formatNumber(count)} ${count === 1 ? singular : plural}`;

const getClockBadge = ({ isClockStatusReliable, clockStatus }) => {
	if (!isClockStatusReliable) {
		return { label: 'Clock status unavailable', tone: 'neutral' };
	}
	if (clockStatus.isClockedIn) {
		return { label: 'Clocked in', tone: 'success' };
	}
	if (clockStatus.hasClockedOutToday) {
		return { label: 'Clocked out', tone: 'neutral' };
	}

	return { label: 'Not clocked in', tone: 'warning' };
};

/**
 * Picking My Day (picker tab): today's picking work from the classic picker
 * board (usePickerMyDay) in the redesigned frame. Same data, Mine / All scope,
 * search, counts, clock gate, pick list and label actions.
 */
const PickingMyDayView = () => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const { hasPermission } = useUserContext();
	const { refetchModeData, canViewEmployeeDays, openEmployeeSelector, tabBarVariant } = useDriverShell();
	const pickerDay = usePickerMyDay({ refetchModeData });
	const {
		userId,
		todayDisplayLabel,
		isFirstLoad,
		hasData,
		error,
		refreshPage,
		isRefreshing,
		canUseClockStatus,
		rosterShift,
		shouldHideWorkForClock,
		clockStatus,
		openClockPass,
		canViewPendingStock,
		isMineMode,
		showMine,
		showAll,
		myCount,
		allCount,
		searchValue,
		setSearchValue,
		routeMachineCount,
		pendingCount,
		labelOnlyCount,
		completedCount,
		myStats,
		filteredRouteGroups,
		filteredCompletedRouteGroups,
		hasCompletedWork,
		openPickingWork,
		openCompletedPick,
		printingLabelDocketId,
	} = pickerDay;
	const clockBadge = getClockBadge(pickerDay);
	const isSearching = Boolean(searchValue.trim());
	// The classic board linked to the service run list; pickers now have it as
	// the All Runs tab, so the link only shows where the bar has no All Runs tab
	// (drivers helping with picking).
	const hasAllRunsTab = Boolean(TAB_BAR_TABS[tabBarVariant]?.includes(APP_TAB.ALL_RUNS)) &&
		hasPermission(ROLE_PERMISSIONS.VIEW_SERVICE_RUN_LIST_PAGE);
	const stats = [
		{ key: 'machines', label: 'Route machines', value: routeMachineCount },
		{ key: 'pending', label: 'Pending', value: pendingCount },
		{ key: 'labelOnly', label: 'Label only', value: labelOnlyCount },
		{ key: 'completed', label: 'Completed POS', value: completedCount },
	];
	// "Completed today" counts what the search leaves, like the list under it.
	const filteredCompletedCount = filteredCompletedRouteGroups.reduce((count, group) => count + group.rows.length, 0);
	const showMyStats = Boolean(myStats && (myStats.today?.posCount > 0 || myStats.last30Days?.posCount > 0));

	const renderWork = () => (
		<>
			<View style={styles.scope}>
				<SegmentedControl
					options={[
						{ value: 'mine', label: `Mine (${formatNumber(myCount)})` },
						{ value: 'all', label: `All (${formatNumber(allCount)})` },
					]}
					value={isMineMode ? 'mine' : 'all'}
					onChange={value => (value === 'mine' ? showMine() : showAll())}
				/>
				<Text style={styles.scopeHint}>
					{isMineMode
						? 'Showing routes with your remaining work. Coworkers\' remaining machines are view-only.'
						: 'Showing remaining picks and repair-only labels in today\'s picking queue.'}
				</Text>
			</View>
			<Card style={styles.statsCard}>
				{stats.map(stat => (
					<View key={stat.key} style={styles.stat} accessible={true} accessibilityLabel={`${stat.label}: ${stat.value}`}>
						<Text style={styles.statValue}>{formatNumber(stat.value)}</Text>
						<Text style={styles.statLabel}>{stat.label}</Text>
					</View>
				))}
			</Card>
			{showMyStats && (
				<Card style={styles.myStats}>
					<Text style={styles.myStatsTitle}>My picking stats</Text>
					<Text style={styles.myStatsText}>
						Today: {formatNumber(myStats.today?.posCount || 0)} POS · {formatNumber(myStats.today?.units || 0)} units
						{myStats.today?.pickingTimeMilliseconds > 0 ? ` · ${printMillisecondsNicely(myStats.today.pickingTimeMilliseconds)}` : ''}
					</Text>
					{myStats.last30Days?.posCount > 0 && (
						<Text style={styles.myStatsText}>
							30 days: {printMillisecondsNicely(myStats.last30Days.averageTimePerPOSMilliseconds || 0) || '0 s'} per POS · {printMillisecondsNicely(myStats.last30Days.averageTimePer100UnitsMilliseconds || 0) || '0 s'} per 100 units · {myStats.last30Days.averageUnitsPerActiveDay || 0} units/day
						</Text>
					)}
				</Card>
			)}
			<TextInput
				value={searchValue}
				onChange={setSearchValue}
				placeholder="Search machine, route, person or date"
			/>
			{(canViewPendingStock || !hasAllRunsTab) && (
				<View style={styles.links}>
					{canViewPendingStock && (
						<ListRow
							icon="inbox"
							title="Pending stock"
							subtitle="Process driver returns back into the warehouse."
							onPress={() => navigation.push('PendingStockListView')}
						/>
					)}
					{!hasAllRunsTab && (
						<ListRow
							icon="list"
							title="All Runs"
							subtitle="Open the service run list."
							onPress={() => navigation.push(DRIVER_ROUTE.ALL_RUNS)}
						/>
					)}
				</View>
			)}
			<View style={styles.section}>
				<SectionTitle title="Routes to prepare" meta={formatCount(filteredRouteGroups.length, 'route', 'routes')} />
				{filteredRouteGroups.length === 0 && (
					<EmptyState
						icon={isSearching ? 'search' : 'package'}
						title={isSearching ? 'No routes found' : 'Nothing to prepare'}
						message={isSearching
							? 'No picking routes found with this search.'
							: isMineMode
								? 'No picking or label work is assigned to you for today.'
								: 'No picking or label work is planned for today.'}
					/>
				)}
			</View>
			{filteredRouteGroups.map(group => (
				<PickingRouteGroup
					key={group.id}
					group={group}
					userId={userId}
					printingLabelDocketId={printingLabelDocketId}
					onOpenRow={openPickingWork}
				/>
			))}
			{hasCompletedWork && (
				<>
					<View style={styles.section}>
						<SectionTitle title="Completed today" meta={`${formatNumber(filteredCompletedCount)} POS`} />
						{filteredCompletedRouteGroups.length === 0 && (
							<EmptyState icon="search" title="No completed picks found" message="No completed picks match this search." />
						)}
					</View>
					{filteredCompletedRouteGroups.map(group => (
						<PickingRouteGroup
							key={`completed-${group.id}`}
							group={group}
							userId={userId}
							isCompleted={true}
							onOpenRow={openCompletedPick}
						/>
					))}
				</>
			)}
		</>
	);

	return (
		<Screen
			backLabel="Home"
			heading="Picking My Day"
			subheading={`Picking work for ${todayDisplayLabel}`}
			isLoading={isFirstLoad}
			error={!hasData && error ? 'Today\'s picking work could not be loaded. Check your connection and try again.' : undefined}
			onRetry={refreshPage}
			refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={refreshPage} />}
		>
			{(canUseClockStatus || canViewEmployeeDays) && (
				<View style={styles.status}>
					{canUseClockStatus && (
						<View style={styles.badges}>
							<StatusBadge label={clockBadge.label} tone={clockBadge.tone} />
							{Boolean(rosterShift) && (
								<Tag label={`Shift ${formatShiftRange(rosterShift.startMinutes, rosterShift.endMinutes)}`} size="regular" />
							)}
						</View>
					)}
					{canViewEmployeeDays && (
						<SecondaryButton label="View an employee's day" icon="users" onPress={openEmployeeSelector} />
					)}
				</View>
			)}
			{shouldHideWorkForClock ? (
				<Card tone="current" style={styles.clockCard}>
					<Text style={styles.clockTitle}>
						{clockStatus.hasClockedOutToday ? 'Clock back in to see your picks' : 'Clock in to see your picks'}
					</Text>
					<Text style={styles.clockText}>Use Shift pass to clock in, then your assigned routes will appear here.</Text>
					<PrimaryButton label="Open shift pass" icon="grid" size="compact" onPress={openClockPass} />
				</Card>
			) : renderWork()}
			<PickerLabelDialogs
				labelDialog={pickerDay.labelDialog}
				completedPickDialog={pickerDay.completedPickDialog}
			/>
		</Screen>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	status: {
		gap: space.md,
	},
	badges: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: space.sm,
	},
	clockCard: {
		gap: space.sm,
	},
	clockTitle: {
		...type.cardTitle,
		color: colors.text,
	},
	clockText: {
		...type.secondary,
		color: colors.textSecondary,
		marginBottom: space.xs,
	},
	scope: {
		gap: space.sm,
	},
	scopeHint: {
		...type.secondary,
		color: colors.textSecondary,
	},
	statsCard: {
		flexDirection: 'row',
		gap: space.sm,
		paddingHorizontal: space.md,
	},
	stat: {
		flex: 1,
		gap: 2,
	},
	statValue: {
		...type.cardTitle,
		color: colors.text,
	},
	statLabel: {
		...type.caption,
		color: colors.textSecondary,
	},
	myStats: {
		gap: space.xs,
	},
	myStatsTitle: {
		...type.bodySmallSemibold,
		color: colors.text,
	},
	myStatsText: {
		...type.secondary,
		color: colors.textSecondary,
	},
	links: {
		gap: space.sm,
	},
	section: {
		gap: space.sm,
	},
});

export default PickingMyDayView;
